import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RecommendedProduct {
  id: string;
  title: string;
  description: string | null;
  price: number;
  currency: string | null;
  images: string[] | null;
  category: string | null;
  stock: number | null;
  views: number | null;
  likes: number | null;
  seller_id: string;
  recommendation_type?: string;
}

export function useRecommendations(limit: number = 8) {
  const [products, setProducts] = useState<RecommendedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const { data, error: rpcError } = await supabase.rpc('get_product_recommendations', {
          p_limit: limit
        });

        if (rpcError) throw rpcError;
        setProducts(data || []);
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setError('Failed to load recommendations');
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [limit]);

  return { products, loading, error };
}

export function useSimilarProducts(productId: string | undefined, limit: number = 4) {
  const [products, setProducts] = useState<RecommendedProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const fetchSimilar = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.rpc('get_similar_products', {
          p_product_id: productId,
          p_limit: limit
        });

        if (error) throw error;
        setProducts(data || []);
      } catch (err) {
        console.error('Error fetching similar products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilar();
  }, [productId, limit]);

  return { products, loading };
}
